import React, { useCallback, useEffect, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useQuery } from 'react-query'

import { api_gateway } from '../../../conexoes/api'
import { useGlobal } from '../../../stores/useGlobal'
import { MascaraCep } from '../../../utils/mascarasCampos'
import MensagemSucesso from '../../componentesModais/mensagens/mensagemSucesso'
import MapaGoogle from '../../googleMap/googleMapSemInfo'
import Botao from '../../Mantine/Botao'
import Input from '../../Mantine/Input'
import Modal from '../../Mantine/Modal'
import Select from '../../Mantine/Select'

import * as S from './styles'

const CACHE_24_HORAS = 1000 * 60 * 60 * 24

const ALERTAS = {
  padrao: { open: false, mensagem: '' },
  maximo: {
    titulo: 'Atenção!',
    open: true,
    mensagem: 'O máximo permitido para essa ação é de 1 portadora selecionada.'
  },
  invalidas: {
    open: true,
    titulo: 'Atenção!',
    mensagem:
      'Apenas portadoras classificadas como Privadas podem executar essa ação.'
  },
  sucesso: { open: true, isSuccess: true }
}

const latitudeRules = {
  required: 'Latitude é obrigatória',
  pattern: {
    value: /^-?\d{1,3}(\.\d{1,9})?$/,
    message: 'Formato inválido'
  }
}

const longitudeRules = {
  required: 'Longitude é obrigatória',
  pattern: {
    value: /^-?\d{1,3}(\.\d{1,9})?$/,
    message: 'Formato inválido'
  }
}

const DEFAULT_COORDENADAS = { lat: -10.235, lng: -55.9253 }

export default function AdicionarLocalizacaoPortadora({
  refetchPortadoras,
  selecionados,
  setSelecionados,
  simcardSelecionadoIndividual,
  setSimcardSelecionadoIndividual
}) {
  const [alerta, setAlerta] = useState(ALERTAS.padrao)
  const {
    setMostrarAcao,
    setMostrarNotificacao,
    setIsLoadingGlobal,
    selecionarParqueInteiro
  } = useGlobal()
  const [coordenadas, setCoordenadas] = useState([])
  const [zoom, setZoom] = useState(14)

  const { data: opcoesEstados } = useQuery(
    ['estados-adicionar-localizacao'],
    async () => {
      const { data } = await api_gateway.get('/m1/consultar/uf')
      return data.dados?.map((uf) => ({
        value: uf.cd_pav_uf,
        label: uf.ds_pav_uf,
        ds_pav_uf: uf.ds_pav_uf
      }))
    },
    {
      staleTime: CACHE_24_HORAS,
      cacheTime: CACHE_24_HORAS,
      refetchOnWindowFocus: false
    }
  )

  const { control, handleSubmit, errors, watch, setError, clearError } =
    useForm({
      defaultValues: {
        latitude: '',
        longitude: '',
        rua: '',
        numero: '',
        bairro: '',
        cidade: '',
        estado: '',
        cep: ''
      }
    })

  const latitude = watch('latitude')
  const longitude = watch('longitude')

  useEffect(() => {
    const lat = parseFloat(latitude)
    const lng = parseFloat(longitude)

    const isLatValida = !isNaN(lat) && lat >= -90 && lat <= 90
    const isLngValida = !isNaN(lng) && lng >= -180 && lng <= 180

    if (isLatValida && isLngValida) {
      setCoordenadas([{ lat, lng }])
      setZoom(14)
    } else {
      setCoordenadas([DEFAULT_COORDENADAS])
      setZoom(3)
    }
  }, [latitude, longitude])

  const portadorasSelecionadas = simcardSelecionadoIndividual.length
    ? simcardSelecionadoIndividual
    : selecionados

  const atualizarSimcards = useCallback(() => {
    const hasInvalidPortadora = portadorasSelecionadas.some(
      (item) => item.portadoraTipo !== 'Privada'
    )

    if (hasInvalidPortadora) {
      setAlerta(ALERTAS.invalidas)
      return
    }

    if (portadorasSelecionadas?.length > 1 || selecionarParqueInteiro) {
      setAlerta(ALERTAS.maximo)
      return
    }
  }, [portadorasSelecionadas, selecionarParqueInteiro])

  useEffect(() => {
    atualizarSimcards()
  }, [portadorasSelecionadas, atualizarSimcards, selecionarParqueInteiro])

  const handleFecharMensagem = () => {
    setAlerta(ALERTAS.padrao)
    handleClose()
  }

  const handleClose = () => {
    setMostrarAcao(undefined)
    setSimcardSelecionadoIndividual([])
  }

  const handleSucesso = () => {
    handleFecharMensagem()
    refetchPortadoras()
  }

  const onSubmit = async (data) => {
    const linhas = simcardSelecionadoIndividual.length
      ? simcardSelecionadoIndividual
      : selecionados

    const cdPortadorasSelecionadas = linhas.map((item) => item.id)

    const siglaEstado = opcoesEstados.find((item) => item.value === data.estado)

    setIsLoadingGlobal(true)

    api_gateway
      .post('/api/v2/carriers/updateCarrierLocation', {
        carrierID: cdPortadorasSelecionadas,
        latitude: data.latitude,
        longitude: data.longitude,
        state: siglaEstado.ds_pav_uf,
        city: data.cidade,
        street: data.rua || '',
        number: data.numero || '',
        district: data.bairro || '',
        cep: data.cep || ''
      })
      .then(() => {
        if (selecionados.length > 0) {
          const lat = parseFloat(data.latitude)
          const lng = parseFloat(data.longitude)

          const atualizados = linhas.map((item) => ({
            ...item,
            latitude: lat,
            longitude: lng,
            logradouro: data.rua || '',
            uf: siglaEstado.ds_pav_uf,
            cidade: data.cidade,
            bairro: data.bairro,
            numero: data.numero || ''
          }))

          setSelecionados(atualizados)
        }

        setAlerta(ALERTAS.sucesso)
      })
      .catch(() => {
        setMostrarNotificacao({
          show: true,
          type: 'error',
          title: 'Falha ao adicionar localização',
          message: 'Não foi possível adicionar a localização, tente novamente.'
        })
      })
      .finally(() => {
        setIsLoadingGlobal(false)
      })
  }

  const handleChangeLatitude = (value) => {
    value = value.trim()
    const num = parseFloat(value)

    if (!value) {
      setError('latitude', 'manual', 'Latitude é obrigatória')
      setCoordenadas([DEFAULT_COORDENADAS])
      setZoom(3)
    } else if (
      !/^-?\d{1,3}(\.\d{1,9})?$/.test(value) ||
      isNaN(num) ||
      num < -90 ||
      num > 90
    ) {
      setError('latitude', 'manual', 'Formato inválido')
      setCoordenadas([DEFAULT_COORDENADAS])
      setZoom(3)
    } else {
      clearError('latitude')
      setCoordenadas([
        { lat: num, lng: parseFloat(longitude) || DEFAULT_COORDENADAS.lng }
      ])
      setZoom(14)
    }

    return value
  }

  const handleChangeLongitude = (value) => {
    value = value.trim()
    const num = parseFloat(value)

    if (!value) {
      setError('longitude', 'manual', 'Longitude é obrigatória')
      setCoordenadas([DEFAULT_COORDENADAS])
      setZoom(3)
    } else if (
      !/^-?\d{1,3}(\.\d{1,9})?$/.test(value) ||
      isNaN(num) ||
      num < -180 ||
      num > 180
    ) {
      setError('longitude', 'manual', 'Formato inválido')
      setCoordenadas([DEFAULT_COORDENADAS])
      setZoom(3)
    } else {
      clearError('longitude')
      setCoordenadas([
        { lat: parseFloat(latitude) || DEFAULT_COORDENADAS.lat, lng: num }
      ])
      setZoom(14)
    }

    return value
  }

  if (selecionados.length === 0 && simcardSelecionadoIndividual === 0)
    return null

  return (
    <>
      {!alerta.open && (
        <Modal
          width="1000px"
          titulo="Adicionar Localização"
          opened
          close={handleClose}
          onConfirm={handleSubmit(onSubmit)}
        >
          <S.ContentWrapper>
            <S.Row>
              <S.LatLongWrapper>
                <S.InputsWrapper>
                  <Controller
                    as={Input}
                    withAsterisk
                    control={control}
                    maxLength={12}
                    name="latitude"
                    label="Latitude"
                    placeholder="Digite a latitude"
                    rules={latitudeRules}
                    error={errors.latitude && errors.latitude.message}
                    onChange={([e]) => handleChangeLatitude(e.target.value)}
                  />

                  <Controller
                    as={Input}
                    withAsterisk
                    control={control}
                    maxLength={12}
                    name="longitude"
                    label="Longitude"
                    placeholder="Digite a longitude"
                    rules={longitudeRules}
                    error={errors.longitude && errors.longitude.message}
                    onChange={([e]) => handleChangeLongitude(e.target.value)}
                  />
                </S.InputsWrapper>
                <S.MapaWrapper>
                  <MapaGoogle
                    zoom={zoom}
                    defaultLatLong={zoom === 3}
                    array={coordenadas}
                  />
                </S.MapaWrapper>
              </S.LatLongWrapper>
            </S.Row>

            <S.Row>
              <Controller
                as={Input}
                name="cep"
                control={control}
                label="CEP"
                placeholder="Digite o CEP"
                maxLength={9}
                onChange={([e]) => MascaraCep(e.target.value)}
              />
              <Controller
                as={Input}
                name="rua"
                control={control}
                label="Rua"
                maxLength={100}
                placeholder="Digite a rua"
              />
              <Controller
                as={Input}
                name="numero"
                control={control}
                maxLength={20}
                label="Número"
                placeholder="Digite o número"
              />
            </S.Row>

            <S.Row>
              <Controller
                as={Input}
                name="bairro"
                control={control}
                maxLength={40}
                label="Bairro"
                placeholder="Digite o bairro"
              />

              <Controller
                as={Input}
                withAsterisk
                name="cidade"
                control={control}
                label="Cidade"
                maxLength={40}
                placeholder="Digite a cidade"
                rules={{ required: 'Cidade é obrigatória' }}
                error={errors.cidade && errors.cidade.message}
              />
              <Controller
                name="estado"
                control={control}
                rules={{ required: 'Estado é obrigatório' }}
                onChange={([selected]) => selected}
                as={
                  <Select
                    withAsterisk
                    searchable
                    options={opcoesEstados}
                    placeholder="Selecione"
                    label="Estado"
                    error={errors.estado && errors.estado.message}
                  />
                }
              />
            </S.Row>
          </S.ContentWrapper>
        </Modal>
      )}

      {alerta.open && !alerta.isSuccess && (
        <Modal
          opened={alerta.open}
          width="445px"
          minHeight={alerta.titulo ? '180px' : '130px'}
          type="alerta"
        >
          <S.ContainerAlerta>
            {alerta.titulo && <S.TituloAlerta>{alerta.titulo}</S.TituloAlerta>}
            <S.MensagemAlerta>{alerta.mensagem}</S.MensagemAlerta>
            <Botao label="Fechar" onClick={handleFecharMensagem} />
          </S.ContainerAlerta>
        </Modal>
      )}

      {alerta.open && alerta.isSuccess && (
        <div className="componente-modal-overlay">
          <div className="componente-modal">
            <MensagemSucesso
              handleMensagemSucesso={handleSucesso}
              mensagemTitulo="Sucesso"
              mensagemParagrafo1="Localização adicionada com sucesso."
              mensagemBotao="Continuar"
            />
          </div>
        </div>
      )}
    </>
  )
}
